import React from "react";
import { X } from "@phosphor-icons/react";
import { relativeTime, untilTime } from "./deliveryHealthHelpers";

function PendingRetriesTable({ pending, selected, setSelected, onRetry, onCancel }) {
  const anyRow = pending.length > 0;
  const allSelected = anyRow && pending.every((a) => selected[a.id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">Pending retries ({pending.length})</div>
        {anyRow && (
          <div className="flex items-center gap-2">
            <button onClick={onRetry} data-testid="drill-retry-now"
              className="border border-black bg-black text-white px-3 py-1 text-[10px] uppercase tracking-widest font-bold hover:bg-yellow-500 hover:text-black">
              Retry {selectedCount || "all"} now
            </button>
            <button onClick={onCancel} data-testid="drill-cancel"
              className="border border-red-700 text-red-800 px-3 py-1 text-[10px] uppercase tracking-widest font-bold hover:bg-red-700 hover:text-white">
              Cancel {selectedCount || "all"}
            </button>
          </div>
        )}
      </div>
      {pending.length === 0 ? (
        <div className="text-xs text-zinc-500">No pending retries.</div>
      ) : (
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-zinc-300 text-[10px] uppercase">
              <th className="text-left py-1 w-6">
                <input type="checkbox" data-testid="drill-select-all"
                  checked={allSelected}
                  onChange={(e) => {
                    const next = {};
                    if (e.target.checked) pending.forEach((a) => { next[a.id] = true; });
                    setSelected(next);
                  }}
                  className="border-2 border-black h-3 w-3" />
              </th>
              <th className="text-left py-1">Attempt</th>
              <th className="text-left py-1">Code</th>
              <th className="text-left py-1">Response</th>
              <th className="text-left py-1">Next retry</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100" data-testid={`drill-pending-${a.id}`}>
                <td className="py-1">
                  <input type="checkbox" data-testid={`drill-select-${a.id}`}
                    checked={!!selected[a.id]}
                    onChange={(e) => setSelected({ ...selected, [a.id]: e.target.checked })}
                    className="border-2 border-black h-3 w-3" />
                </td>
                <td className="py-1">#{a.attempt_no}</td>
                <td className="py-1">{a.response_code || "—"}</td>
                <td className="py-1 truncate max-w-[240px]">{a.response_body?.slice(0, 120) || "—"}</td>
                <td className="py-1">
                  <span className={untilTime(a.next_retry_at) === "overdue" ? "text-red-700 font-bold" : ""}>
                    {untilTime(a.next_retry_at)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RecentFailuresTable({ failures }) {
  return (
    <div>
      <div className="label-mono mb-2">Recent failures ({failures.length})</div>
      {failures.length === 0 ? (
        <div className="text-xs text-zinc-500">No recorded failures.</div>
      ) : (
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-zinc-300 text-[10px] uppercase">
              <th className="text-left py-1">When</th>
              <th className="text-left py-1">Status</th>
              <th className="text-left py-1">Code</th>
              <th className="text-left py-1">Response</th>
            </tr>
          </thead>
          <tbody>
            {failures.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100" data-testid={`drill-failure-${a.id}`}>
                <td className="py-1">{relativeTime(a.created_at)}</td>
                <td className="py-1">{a.status}</td>
                <td className="py-1">{a.response_code || "—"}</td>
                <td className="py-1 truncate max-w-[280px]">{a.response_body?.slice(0, 120) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Modal shown when a destination row is clicked. Displays pending retries
 * with bulk retry/cancel + a recent-failures log.
 */
export default function DeliveryDrillModal({
  drill, busy, selected, setSelected, onClose, onBulkAction,
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose} data-testid="drill-modal">
      <div className="bg-white brut-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-mono">Delivery queue</div>
            <div className="font-chivo font-black text-2xl uppercase tracking-tight">
              {drill?.destination?.name || "…"}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">{drill?.destination?.type}</div>
          </div>
          <button onClick={onClose} data-testid="drill-close"
            className="border border-black p-2 hover:bg-black hover:text-white">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="overflow-auto p-4 space-y-6">
          {busy && <div className="label-mono">Loading…</div>}
          {drill && (
            <>
              <PendingRetriesTable
                pending={drill.pending_retries}
                selected={selected}
                setSelected={setSelected}
                onRetry={() => onBulkAction("retry-now")}
                onCancel={() => onBulkAction("cancel")}
              />
              <RecentFailuresTable failures={drill.recent_failures} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
